import { useTranslation } from '@hopin-team/provider-translation';
import axios, { AxiosError } from 'axios';
import React, { createContext, useEffect, useRef, useState } from 'react';

import { useAuthentication } from '../hooks/use-authentication';
import {
  setAuthorizationHeader,
  setBaseUrl,
  setOrgId,
} from '../services/http-client';
import { getToken } from '../services/users';

type AuthenticationState = {
  status: 'idle' | 'pending' | 'complete';
  token: string | null;
  organisationId: string;
};

const initialAuthState: AuthenticationState = {
  status: 'idle',
  token: null,
  organisationId: '',
};

export const AuthenticationContext =
  createContext<AuthenticationState>(initialAuthState);

type AuthenticationProviderProps = {
  children: React.ReactNode;
  host: string;
  organisationId: string;
};

export const AuthenticationProvider = ({
  children,
  host,
  organisationId,
}: AuthenticationProviderProps) => {
  const mounted = useRef(false);
  const [authState, setAuthState] = useState<AuthenticationState>({
    ...initialAuthState,
    organisationId,
  });

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (authState.status !== 'idle') return;

    const requestAuthToken = async () => {
      if (!mounted.current) {
        return;
      }

      setAuthState({ ...authState, status: 'pending' });

      try {
        const authResponse = await getToken(organisationId);

        setAuthorizationHeader(authResponse.token);
        setBaseUrl(authResponse.endpoints.analytics);
        setOrgId(organisationId);

        if (mounted.current) {
          setAuthState({
            ...authState,
            status: 'complete',
            token: authResponse.token,
          });
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if ((error as AxiosError).response?.status === 401) {
            window.location.assign(`${host}/sign_in`);
          }
        }
      }
    };

    requestAuthToken();
  }, [authState, host, organisationId]);

  return (
    <AuthenticationContext.Provider value={authState}>
      {children}
    </AuthenticationContext.Provider>
  );
};

const Loading = ({ children }: React.PropsWithChildren<unknown>) => {
  const { isLoading } = useAuthentication();
  const { t } = useTranslation();

  if (isLoading) {
    return <div className="text-white">{t('loading')}</div>;
  }

  return <>{children}</>;
};

AuthenticationProvider.Loading = Loading;
